import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  Plus,
  ArrowRight,
  TrendingUp,
  Building2,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConsultantDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentInterventions, setRecentInterventions] = useState([]);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, interventionsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/interventions`),
        axios.get(`${API}/sme-companies`)
      ]);
      setStats(statsRes.data);
      setRecentInterventions(interventionsRes.data.slice(0, 5));
      setAssignedCompanies(companiesRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { styles: 'bg-orange-100 text-orange-700 border-orange-200', label: 'En attente' },
      validated: { styles: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Validée' },
      rejected: { styles: 'bg-red-100 text-red-700 border-red-200', label: 'Rejetée' }
    };
    const { styles, label } = config[status];
    return (
      <Badge variant="outline" className={styles} data-testid={`status-badge-${status}`}>
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="consultant-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Tableau de bord</h1>
            <p className="text-slate-500 mt-1">Suivez vos interventions de conseil</p>
          </div>
          <Button 
            asChild 
            className="bg-orange-500 hover:bg-orange-600 text-white"
            disabled={assignedCompanies.length === 0}
          >
            <Link to="/consultant/interventions/new" data-testid="new-intervention-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle intervention
            </Link>
          </Button>
        </div>

        {/* Alert if no companies assigned */}
        {assignedCompanies.length === 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-orange-800 font-medium">Aucune PME attribuée</p>
                <p className="text-orange-600 text-sm">
                  Contactez votre administrateur pour obtenir des PME à gérer.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 shadow-sm" data-testid="stat-companies">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium uppercase tracking-wider">PME attribuées</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{assignedCompanies.length}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-total">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Interventions</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_interventions || 0}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium uppercase tracking-wider">En attente</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.pending_validations || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-hours">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Heures totales</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_hours?.toFixed(1) || 0}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Assigned Companies */}
        {assignedCompanies.length > 0 && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="text-xl font-semibold text-slate-900">Mes PME</CardTitle>
                <CardDescription className="text-slate-500">Entreprises dont vous êtes responsable</CardDescription>
              </div>
              <Button variant="ghost" asChild className="text-slate-600 hover:text-slate-900">
                <Link to="/consultant/companies" data-testid="view-all-companies">
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {assignedCompanies.slice(0, 6).map((company) => (
                  <div 
                    key={company.id}
                    className="p-4 bg-slate-50 rounded-lg"
                    data-testid={`company-${company.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{company.name}</p>
                        <p className="text-sm text-slate-500">{company.contact_person || 'Pas de contact'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Interventions */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <div>
              <CardTitle className="text-xl font-semibold text-slate-900">Interventions récentes</CardTitle>
              <CardDescription className="text-slate-500">Vos dernières activités de conseil</CardDescription>
            </div>
            <Button variant="ghost" asChild className="text-slate-600 hover:text-slate-900">
              <Link to="/consultant/interventions" data-testid="view-all-interventions">
                Voir tout <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentInterventions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune intervention</p>
                {assignedCompanies.length > 0 && (
                  <Button asChild variant="outline" className="mt-4">
                    <Link to="/consultant/interventions/new">Créer votre première intervention</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {recentInterventions.map((intervention) => (
                  <Link 
                    key={intervention.id} 
                    to={`/consultant/interventions/${intervention.id}`}
                    className="block"
                    data-testid={`intervention-${intervention.id}`}
                  >
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <p className="font-medium text-slate-900 truncate">{intervention.sme_company_name}</p>
                          {getStatusBadge(intervention.status)}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {intervention.intervention_type} • {intervention.date} • {intervention.duration_hours}h
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
